/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Component } from 'react';
import SharedStyles from '../styles';
import FormTitle from '../form-title';
import { Table } from 'antd';
import { size } from 'lodash';
import { ColumnProps } from 'antd/lib/table';

export interface TableColumnFilter {
  text: string,
  value: string,
  children?: Array<TableColumnFilter>,
}

interface IProps<T> {
  columns: ColumnProps<T>[];
  data: T[];
  canSelectRows?: boolean;
  loading?: boolean;
  implementScroll?: boolean;
  showPagination?: boolean;
  scrollHeight?: number;
  pageSize?: number;
  title?: string;
  hidePageSelect?: boolean;
  // tslint:disable-next-line:no-any
  rowClick?: (record: any, rowIndex: number) => void;
  onSelectedRowsChange?: (keys: string[] | number[], rows: any[]) => void;
  // tslint:disable-next-line:no-any
  rowKey?: (record: any) => string;
  includeCount?: boolean | string | ((amt: number) => string);
  countExtra?: string;
  subtractHeight?: number;
  getCheckboxProps?: (record: any) => any;
  // AER-1612: optional controlled pagination — when both are supplied, the
  // table's current page is driven by the parent (e.g. persisted in Redux)
  // instead of antd's internal Table state. Other consumers that don't pass
  // these props keep the previous uncontrolled behavior.
  currentPage?: number;
  onPageChange?: (page: number) => void;
  // AER-1612 follow-up: opt-in controlled page size. When the parent passes
  // `controlledPageSize`, the antd <Table> is driven from props and the
  // parent persists the value (Dashboard → `dash.view.equipPageSize`).
  // Uncontrolled callers leave both undefined and keep AER-1607's local-
  // state behavior so the size selector still sticks within the lifetime
  // of the component instance.
  controlledPageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
}

export class TableList<T> extends Component<IProps<T>> {
  state = {
    selectedRowKeys: [],
    selectedRows: [],
    loading: false,
    height: 500,
    pageSize: this.props.pageSize || 100,
  };
  mounted = true;
  containerRef: HTMLDivElement | null = null;
  calcTableSize = () => {
    const container = document.getElementById('ContentContainer');

    if (container) {
      const cHeight = container.offsetHeight;
      const subtract = this.props.subtractHeight || 200;
      const height = cHeight - subtract;
      this.setState({
        height,
      });
    }
  };
  componentWillUnmount = () => {
    this.mounted = false;
    window.removeEventListener('resize', this.calcTableSize);
  };
  componentDidMount = () => {
    setTimeout(() => {
      if (this.mounted) {
        this.calcTableSize();
      }
    }, 500);
    window.addEventListener('resize', this.calcTableSize);
  };
  selectedRowsChange = () => {
    const { onSelectedRowsChange } = this.props;
    if (onSelectedRowsChange) {
      onSelectedRowsChange(this.state.selectedRowKeys, this.state.selectedRows);
    }
  };
  rowSelection = () => {
    const { selectedRowKeys } = this.state;

    return {
      selectedRowKeys,
      onChange: (keys: any[], selectedRows: T[]) => {
        this.setState({ selectedRowKeys: keys, selectedRows }, this.selectedRowsChange);
      },
      getCheckboxProps: (row: T) => {
        return this.props.getCheckboxProps ? this.props.getCheckboxProps(row) : undefined;
      }
    };
  };
  // tslint:disable-next-line:no-any
  rowClick = (record: any, rowIndex: number) => {
    const { rowClick } = this.props;
    if (rowClick) {
      rowClick(record, rowIndex);
    }
  };

  static defaultProps = {
    canSelectRows: true,
    implementScroll: true,
    showPagination: true,
    pageSize: 100,
    includeCount: true,
    rowKey: (r: { sk: string | undefined, id: string | undefined }) => r.sk || r.id,
  };

  renderCount = () => {
    const { includeCount, data, showPagination, countExtra } = this.props;

    if (includeCount) {
      const amt = size(data);
      const str =
        typeof includeCount === 'string'
          ? `${includeCount}: ${amt}`
          : typeof includeCount === 'function'
            ? includeCount(amt)
            : `Count: ${amt}`;
      const extra = countExtra || null;

      // AER-1612 follow-up: match the same precedence as the antd pagination
      // prop so the count alignment doesn't briefly mis-position when the
      // controlled page size differs from the local-state default.
      const effectivePageSize =
        this.props.controlledPageSize !== undefined
          ? this.props.controlledPageSize
          : this.state.pageSize;
      return (
        <div
          css={css(
            `margin: 0; margin-top: ${showPagination && amt > effectivePageSize ? -40 : 10
            }px; font-weight: 500; width: 100%; text-align: left;`
          )}
        >
          <span dangerouslySetInnerHTML={{ __html: str }} />
          {!!extra && <div>{extra}</div>}
        </div>
      );
    }

    return null;
  };
  render() {
    const { height } = this.state;
    const {
      columns,
      data,
      canSelectRows,
      loading,
      implementScroll,
      showPagination,
      scrollHeight,
      rowClick,
      title,
      rowKey,
    } = this.props;

    return (
      <div ref={el => { this.containerRef = el; }}>
        {title && (
          <div css={css(SharedStyles.row, `height: 30px;`)}>
            <FormTitle
              css={css`
                margin-bottom: 0;
              `}
              title={title}
            />
          </div>
        )}
        <Table
          rowKey={rowKey}
          onRow={(record, rowIndex) => {
            return {
              onClick: () => this.rowClick(record, rowIndex!),
            };
          }}
          onChange={(pagination) => {
            const nextPageSize = pagination && pagination.pageSize;
            if (nextPageSize && nextPageSize !== this.state.pageSize) {
              this.setState({ pageSize: nextPageSize });
            }
            // AER-1612 follow-up: notify parent of page-size changes too so
            // Dashboard can persist the user's selection across unmount.
            if (nextPageSize && this.props.onPageSizeChange) {
              this.props.onPageSizeChange(nextPageSize);
            }
            // AER-1612: notify parent of page changes so callers that opt
            // into controlled pagination (Dashboard) can persist the page.
            const nextPage = pagination && pagination.current;
            if (nextPage && this.props.onPageChange) {
              this.props.onPageChange(nextPage);
            }
            const scope = this.containerRef;
            if (scope) {
              const body = scope.querySelector('.ant-table-body') as HTMLElement | null;
              if (body) body.scrollTop = 0;
            }
            const contentEl = document.getElementById('ContentContainer');
            if (contentEl) contentEl.scrollTop = 0;
          }}
          pagination={
            showPagination
              ? {
                  hideOnSinglePage: true,
                  // AER-1612 follow-up: prefer the parent-controlled value
                  // when supplied so the size selector reflects the persisted
                  // choice on remount.
                  pageSize:
                    this.props.controlledPageSize !== undefined
                      ? this.props.controlledPageSize
                      : this.state.pageSize,
                  // AER-1612: when the parent provides `currentPage`, drive
                  // the table's page from props (controlled). Otherwise omit
                  // `current` and let antd manage it internally as before.
                  ...(this.props.currentPage !== undefined
                    ? { current: this.props.currentPage }
                    : {}),
                }
              : false
          }
          size={'small'}
          scroll={implementScroll ? { y: scrollHeight || height } : undefined}
          rowSelection={canSelectRows ? this.rowSelection() : undefined}
          columns={columns}
          dataSource={data}
          loading={loading}
          rowClassName={(_item, index) =>
            `tbl-list-row-${index % 2} ${rowClick ? 'canClick' : ''}`
          }
        />
        {this.renderCount()}
      </div>
    );
  }
}